import { useTranslation } from 'react-i18next';
import { useMonthOptions } from '@/hooks/useMonthOptions';
import { ADMIN_COLORS } from '../utils/adminAnimations';
import type { TimeRange, TimeRangeMode, Quarter } from '../utils/timeRange';
import {
  createMonthlyRange,
  createQuarterlyRange,
  createAnnualRange,
  createCustomRange,
  monthToQuarter,
  monthToYear,
} from '../utils/timeRange';

interface TimeRangePickerProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  compact?: boolean;
  className?: string;
}

const MODES: TimeRangeMode[] = ['monthly', 'quarterly', 'annually', 'custom'];
const QUARTERS: Quarter[] = [1, 2, 3, 4];

export function TimeRangePicker({ value, onChange, compact: _compact, className = '' }: TimeRangePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  const selectedYear = monthToYear(value.startMonth);
  const selectedQuarter = monthToQuarter(value.startMonth);

  const monthOptions = useMonthOptions(locale, 12);

  const handleModeChange = (mode: TimeRangeMode) => {
    if (mode === value.mode) return;
    switch (mode) {
      case 'monthly':
        onChange(createMonthlyRange(value.startMonth));
        break;
      case 'quarterly':
        onChange(createQuarterlyRange(selectedYear, monthToQuarter(value.startMonth)));
        break;
      case 'annually':
        onChange(createAnnualRange(selectedYear));
        break;
      case 'custom':
        onChange(createCustomRange(value.startMonth, value.endMonth));
        break;
    }
  };

  const isCustomValid = value.mode === 'custom' ? value.endMonth >= value.startMonth : true;

  const selectStyle = {
    backgroundColor: 'rgba(255,255,255,0.7)',
    border: `1px solid ${ADMIN_COLORS.border}`,
    color: ADMIN_COLORS.navy,
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Mode chips */}
      <div className="flex flex-wrap gap-1.5">
        {MODES.map(mode => {
          const isActive = value.mode === mode;
          return (
            <button
              key={mode}
              type="button"
              onClick={() => handleModeChange(mode)}
              className="rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all"
              style={{
                backgroundColor: isActive ? 'var(--color-primary-500)' : 'rgba(255,255,255,0.6)',
                color: isActive ? '#fff' : ADMIN_COLORS.navy,
                border: `1px solid ${isActive ? 'var(--color-primary-500)' : ADMIN_COLORS.border}`,
              }}
            >
              {t(`timeRange.${mode}`)}
            </button>
          );
        })}
      </div>

      {/* Mode-specific controls */}
      <div className="flex flex-wrap items-center gap-2">
        {value.mode === 'monthly' && (
          <select
            value={value.startMonth}
            onChange={e => onChange(createMonthlyRange(e.target.value))}
            className="rounded-xl px-3 py-2 text-xs font-medium outline-none"
            style={selectStyle}
          >
            {monthOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        {value.mode === 'quarterly' && (
          <>
            <select
              value={selectedYear}
              onChange={e => onChange(createQuarterlyRange(Number(e.target.value), selectedQuarter))}
              className="rounded-xl px-3 py-2 text-xs font-medium outline-none"
              style={selectStyle}
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <div className="flex gap-1.5">
              {QUARTERS.map(q => {
                const isActive = selectedQuarter === q;
                return (
                  <button
                    key={q}
                    type="button"
                    onClick={() => onChange(createQuarterlyRange(selectedYear, q))}
                    className="rounded-full px-2.5 py-1.5 text-[11px] font-semibold transition-all"
                    style={{
                      backgroundColor: isActive ? 'var(--color-primary-500)' : 'rgba(255,255,255,0.6)',
                      color: isActive ? '#fff' : ADMIN_COLORS.navy,
                      border: `1px solid ${isActive ? 'var(--color-primary-500)' : ADMIN_COLORS.border}`,
                    }}
                  >
                    {t(`timeRange.q${q}`)}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {value.mode === 'annually' && (
          <select
            value={selectedYear}
            onChange={e => onChange(createAnnualRange(Number(e.target.value)))}
            className="rounded-xl px-3 py-2 text-xs font-medium outline-none"
            style={selectStyle}
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        )}

        {value.mode === 'custom' && (
          <>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium" style={{ color: ADMIN_COLORS.textSecondary }}>
                {t('timeRange.startMonth')}
              </span>
              <select
                value={value.startMonth}
                onChange={e => onChange(createCustomRange(e.target.value, value.endMonth < e.target.value ? e.target.value : value.endMonth))}
                className="rounded-xl px-3 py-2 text-xs font-medium outline-none"
                style={selectStyle}
              >
                {monthOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium" style={{ color: ADMIN_COLORS.textSecondary }}>
                {t('timeRange.endMonth')}
              </span>
              <select
                value={value.endMonth}
                onChange={e => onChange(createCustomRange(value.startMonth, e.target.value))}
                className="rounded-xl px-3 py-2 text-xs font-medium outline-none"
                style={selectStyle}
              >
                {monthOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {!isCustomValid && (
              <span className="text-[10px] font-medium" style={{ color: '#dc2626' }}>
                {t('timeRange.rangeInvalid')}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
